import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { $api } from '../../api/queryClient'

/**
 * General assembly hooks (spec 2026-10-05-01). Every assembly read hangs off a
 * contract path, so one invalidation covers them all after any write: the
 * attendance sheet, the votes and the outcome move together (a vote changes the
 * computed outcome, a new co-owner changes the totals).
 */
export function invalidateAssembly(queryClient: QueryClient) {
    return queryClient.invalidateQueries({
        predicate: (query) => {
            const key = query.queryKey as unknown[]
            return (
                key[0] === 'get' &&
                typeof key[1] === 'string' &&
                (key[1].includes('/contracts') || key[1].includes('assembly-decisions') || key[1].includes('assembly-resolutions'))
            )
        },
    })
}

const BASE = '/api/v1/organizations/{orgCode}/contracts/{contractUid}'

export function useAssemblyChildren(org: string, contractUid: string) {
    return $api.useQuery('get', `${BASE}/children` as '/api/v1/organizations/{orgCode}/contracts/{contractUid}/children', {
        params: { path: { orgCode: org, contractUid } },
    })
}

export function useAssemblyAttendance(org: string, contractUid: string) {
    return $api.useQuery('get', `${BASE}/attendance` as '/api/v1/organizations/{orgCode}/contracts/{contractUid}/attendance', {
        params: { path: { orgCode: org, contractUid } },
    })
}

/** What rebuilding the sheet from the property would change (nothing is written). */
export function useAttendancePreview(org: string, contractUid: string, enabled: boolean) {
    return $api.useQuery('get', `${BASE}/attendance/preview` as '/api/v1/organizations/{orgCode}/contracts/{contractUid}/attendance/preview', {
        params: { path: { orgCode: org, contractUid } },
    }, { enabled: enabled && !!org && !!contractUid, gcTime: 0 })
}

export function useAssemblyVotes(org: string, resolutionUid: string) {
    return $api.useQuery('get', `${BASE}/votes` as '/api/v1/organizations/{orgCode}/contracts/{contractUid}/votes', {
        params: { path: { orgCode: org, contractUid: resolutionUid } },
    })
}

export function useAssemblyOutcome(org: string, resolutionUid: string) {
    return $api.useQuery('get', `${BASE}/outcome` as '/api/v1/organizations/{orgCode}/contracts/{contractUid}/outcome', {
        params: { path: { orgCode: org, contractUid: resolutionUid } },
    })
}

/** The contracts that run under a resolution ("what it authorizes"). */
export function useAuthorizedContracts(org: string, resolutionUid: string) {
    return $api.useQuery('get', `${BASE}/authorized-contracts` as '/api/v1/organizations/{orgCode}/contracts/{contractUid}/authorized-contracts', {
        params: { path: { orgCode: org, contractUid: resolutionUid } },
    }, { enabled: !!org && !!resolutionUid })
}

/** Was it voted? The decisions that concern an asset (spec 2026-10-10-08). */
export function useAssetDecisions(
    org: string,
    assetUid: string,
    filters: { q?: string; outcome?: string; subject?: string; link?: string } = {},
) {
    return $api.useQuery('get', '/api/v1/organizations/{orgCode}/assets/{assetUid}/assembly-decisions', {
        params: {
            path: { orgCode: org, assetUid },
            query: {
                q: filters.q || undefined,
                outcome: (filters.outcome || undefined) as 'adopted' | undefined,
                subject: filters.subject || undefined,
                link: (filters.link || undefined) as 'direct' | undefined,
            },
        },
    }, { enabled: !!org && !!assetUid })
}

/** The resolutions read from a PV document. */
export function useDocumentResolutions(org: string, documentUid: string, enabled: boolean) {
    return $api.useQuery('get', '/api/v1/organizations/{orgCode}/documents/{documentUid}/assembly-resolutions', {
        params: { path: { orgCode: org, documentUid } },
    }, { enabled: enabled && !!org && !!documentUid })
}

/** What authorizes the spending of a contract, and how much of its ceiling is consumed. */
export function useContractCoverage(org: string, contractUid: string) {
    return $api.useQuery('get', `${BASE}/coverage` as '/api/v1/organizations/{orgCode}/contracts/{contractUid}/coverage', {
        params: { path: { orgCode: org, contractUid } },
    }, { enabled: !!org && !!contractUid })
}

/** The same, through the contract an intervention runs under. */
export function useInterventionCoverage(org: string, interventionUid: string) {
    return $api.useQuery('get', '/api/v1/organizations/{orgCode}/interventions/{interventionUid}/coverage', {
        params: { path: { orgCode: org, interventionUid } },
    }, { enabled: !!org && !!interventionUid })
}

/** The write hooks all refresh every assembly read on success. */
export function useAssemblyMutations(org: string) {
    const queryClient = useQueryClient()
    const onSuccess = () => invalidateAssembly(queryClient)
    return {
        createChild: $api.useMutation('post', `${BASE}/children` as '/api/v1/organizations/{orgCode}/contracts/{contractUid}/children', { onSuccess }),
        reorder: $api.useMutation('put', `${BASE}/children/order` as '/api/v1/organizations/{orgCode}/contracts/{contractUid}/children/order', { onSuccess }),
        generateAttendance: $api.useMutation('post', `${BASE}/attendance` as '/api/v1/organizations/{orgCode}/contracts/{contractUid}/attendance', { onSuccess }),
        addParty: $api.useMutation('post', `${BASE}/attendance/parties` as '/api/v1/organizations/{orgCode}/contracts/{contractUid}/attendance/parties', { onSuccess }),
        waiveBase: $api.useMutation('post', `${BASE}/attendance/base-waiver` as '/api/v1/organizations/{orgCode}/contracts/{contractUid}/attendance/base-waiver', { onSuccess }),
        removeParty: $api.useMutation('delete', `${BASE}/attendance/{stakeholderUid}` as '/api/v1/organizations/{orgCode}/contracts/{contractUid}/attendance/{stakeholderUid}', { onSuccess }),
        setPresence: $api.useMutation('patch', `${BASE}/attendance/{stakeholderUid}` as '/api/v1/organizations/{orgCode}/contracts/{contractUid}/attendance/{stakeholderUid}', { onSuccess }),
        putVotes: $api.useMutation('put', `${BASE}/votes` as '/api/v1/organizations/{orgCode}/contracts/{contractUid}/votes', { onSuccess }),
        setOutcome: $api.useMutation('put', `${BASE}/outcome` as '/api/v1/organizations/{orgCode}/contracts/{contractUid}/outcome', { onSuccess }),
        recordSigned: $api.useMutation('post', `${BASE}/record-signed` as '/api/v1/organizations/{orgCode}/contracts/{contractUid}/record-signed', { onSuccess }),
        postpone: $api.useMutation('post', `${BASE}/postpone` as '/api/v1/organizations/{orgCode}/contracts/{contractUid}/postpone', { onSuccess }),
        changeHolder: $api.useMutation('post', `${BASE}/ballots/{stakeholderUid}/change-holder` as '/api/v1/organizations/{orgCode}/contracts/{contractUid}/ballots/{stakeholderUid}/change-holder', { onSuccess }),
        approve: $api.useMutation('post', `${BASE}/approve` as '/api/v1/organizations/{orgCode}/contracts/{contractUid}/approve', { onSuccess }),
        org,
    }
}

/** The message of an API error body (`{title|message|detail}`), for inline display. */
export function apiErrorMessage(error: unknown): string {
    if (!error || typeof error !== 'object') return ''
    const e = error as { message?: string; title?: string; detail?: string; error?: string }
    return e.message || e.detail || e.title || e.error || ''
}
